import {useMutation} from "@apollo/client";
import {useCallback, useEffect, useState} from "react";
import {Dialog, useTheme} from "@mui/material";
import {FormattedButton, DialogActions, FormattedDialogTitle, IconDialogContent} from "@dvrt/graphql-widgets";
import {defineMessage, defineMessages, FormattedMessage} from "react-intl";
import {ResyncProvisionedDeviceDocument} from "./queries.generated";
import {ResyncProvisionedDeviceMutation, ResyncProvisionedDeviceMutationVariables} from "./queries.generated";
import {msg_close} from "@/utils/translations";
import {CallControlCommandError, ResynchronizeError} from "@/api";

type Props = {
    deviceIdList: number[];
    isOpen: boolean;
    close: () => void;
    name?: string;
};

type RequestState =
    | {
          stage: "request_sent";
      }
    | {
          stage: "success";
      }
    | {
          stage: "resync_error";
          error: ResynchronizeError;
      }
    | {
          stage: "call_control_command_error";
          error: CallControlCommandError;
      };

const RESYNC_ERROR_MESSAGES = defineMessages<ResynchronizeError>({
    [ResynchronizeError.CallControlCannotConnect]: {
        id: "devices.provisioned.resync.error.call_control_cannot_connect",
        defaultMessage: "Can't connect to the call-control service.",
    },
    [ResynchronizeError.CallControlDisabled]: {
        id: "devices.provisioned.resync.error.call_control_disabled",
        defaultMessage: "Call control is disabled.",
    },
});

const CALL_CONTROLL_COMMAND_ERROR_MESSAGES = defineMessages<CallControlCommandError>({
    CALL_CONTROL_ERROR: {
        id: "devices.provisioned.resync.call_control_error.call_control_error",
        defaultMessage: "Call-control service error, please call the system admin!",
    },
    UNKNOWN_DEVICE: {
        id: "devices.provisioned.resync.call_control_error.unknown_device",
        defaultMessage: "Unknown device, please call the system admin!",
    },
    INCOMPLETE_DATA: {
        id: "devices.provisioned.resync.call_control_error.incomplete_data",
        defaultMessage: "Incomplete data, please call the system admin!",
    },
});

const ResyncProgressDialog = ({deviceIdList, isOpen, close, name}: Props) => {
    const theme = useTheme();
    const [mutation] = useMutation<ResyncProvisionedDeviceMutation, ResyncProvisionedDeviceMutationVariables>(
        ResyncProvisionedDeviceDocument,
    );
    const [requestState, setRequestState] = useState<RequestState>({
        stage: "request_sent",
    });

    const startResync = useCallback(async () => {
        const response = await mutation({variables: {deviceIdList}});

        if (response.data.resynchronizeProvisionedDevices.error) {
            setRequestState({
                stage: "resync_error",
                error: response.data.resynchronizeProvisionedDevices.error,
            });
        } else {
            const error = response.data.resynchronizeProvisionedDevices.devices[0].error;
            if (error) {
                setRequestState({
                    stage: "call_control_command_error",
                    error: response.data.resynchronizeProvisionedDevices.devices[0].error,
                });
            } else {
                setRequestState({
                    stage: "success",
                });
            }
        }
    }, [mutation, deviceIdList]);

    useEffect(() => {
        if (!isOpen) return;

        startResync();
    }, [isOpen, startResync]);

    let dialogContent;
    switch (requestState.stage) {
        case "request_sent":
            dialogContent = (
                <IconDialogContent icon="question-circle" iconColor={theme.palette.primary.main}>
                    <FormattedMessage
                        id="devices.provisioned.resync.pending"
                        defaultMessage="Request sent to the backend. Waiting..."
                    />
                </IconDialogContent>
            );
            break;
        case "resync_error":
        case "call_control_command_error":
            dialogContent = (
                <IconDialogContent icon="exclamation-triangle" iconColor={theme.palette.secondary.main}>
                    <FormattedMessage
                        {...(requestState.stage === "resync_error"
                            ? RESYNC_ERROR_MESSAGES[requestState.error]
                            : CALL_CONTROLL_COMMAND_ERROR_MESSAGES[requestState.error])}
                    />
                </IconDialogContent>
            );
            break;
        case "success":
            dialogContent = (
                <IconDialogContent icon="check-circle" iconColor={theme.palette.success.main}>
                    <FormattedMessage
                        id="devices.provisioned.resync.success"
                        defaultMessage="Resyncronize request has been sent to the device."
                    />
                </IconDialogContent>
            );
            break;
    }

    return (
        <Dialog open={isOpen} onClose={close}>
            {name ? (
                <FormattedDialogTitle
                    title={defineMessage({
                        id: "devices.provisioned.resync.title_single",
                        defaultMessage: "Resynchronize device: {name}",
                    })}
                    values={{name}}
                />
            ) : (
                <FormattedDialogTitle
                    title={defineMessage({
                        id: "devices.provisioned.resync.title_multi",
                        defaultMessage: "Resynchronize {count, number} {count, plural, one {device} other {devices} }",
                    })}
                    values={{count: deviceIdList.length}}
                />
            )}
            {dialogContent}
            <DialogActions>
                <FormattedButton message={msg_close} onClick={close} variant="text" />
            </DialogActions>
        </Dialog>
    );
};

export default ResyncProgressDialog;
